import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  FDBodyText,
  FDDialog, FDDialogActions,
  FDDialogContent,
  FDDialogTitle,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDSecondaryActionIconButton,
  FDStack,
  FDTextButton,
} from '@facet-wealth/design';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useMobileOrMCLayout } from 'Hooks/useMobileLayoutOrMCLayout';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getBorder } from 'Components/utils';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { HouseholdBalanceSheetFilterLozengesLayout } from './HouseholdBalanceSheetFilterLozengesLayout';
import { LozengeMultiPicklist } from './LozengeMultiPicklist';

const useStyles = buildUseStyles('HouseholdBalanceSheetFilterLayout', (theme) => ({
  verticalDivider: {
    ...getBorder(theme, { left: true }),
  },
}));

const lozengesMetaOverrideSet = buildFacetDesignOverride()
  .typeOverride('multipicklist', { _component: () => LozengeMultiPicklist }, overrideReadOnly);

export const HouseholdBalanceSheetFilterLayout = ({
  onSubmit, prompt, loading, open, setOpen, additionalFields = [], hidePrompt = false,
}) => {
  const isMobile = useMobileLayout();
  const isMobileOrMC = useMobileOrMCLayout();
  const { handleChange, resource } = useMetaLayoutContext();
  const classes = useStyles();

  const visibleMultipicklists = resource.meta().template().list().filter(field => field.attr('type') === 'multipicklist' && !field.attr('hidden'));
  const visibleMultipicklistAttrNames = visibleMultipicklists.map(field => field.attr('name'));

  const onClickResetFilters = () => {
    for (const attr of visibleMultipicklistAttrNames) {
      handleChange(attr, []);
    }
    for (const field of additionalFields) {
      handleChange(field.name, field.resetTo);
    }
  };

  const onClickClearFilters = async () => {
    onClickResetFilters();
    onSubmit();
  };

  let filtersLabel = 'Filters';
  // count the number of applied filters from query
  const numAppliedMultipicklistFilters = visibleMultipicklists.flatMap(field => resource.attr(field.id()) ?? []).length;
  const numAppliedFilters = numAppliedMultipicklistFilters + additionalFields.filter(field => resource.attr(field.name) !== field.resetTo).length;
  const isFiltering = numAppliedFilters > 0;
  if (isFiltering) {
    filtersLabel += ` (${numAppliedFilters})`;
  }

  const getFiltersButton = () => {
    return isMobileOrMC ? (
      <FDSecondaryActionIconButton
        icon="filterList"
        onClick={() => setOpen(true)}
        label={filtersLabel}
      />
    ) : (
      <FDSecondaryActionButton
        size="sm"
        label={filtersLabel}
        icon="filterList"
        iconPosition="start"
        onClick={() => setOpen(true)}
      />
    );
  };

  const topRow = (isMobile ? ['ownerId'] : []).filter(attr => visibleMultipicklistAttrNames.includes(attr));
  const bottomRow = (isMobile ? ['assetType', 'liabilityType'] : ['ownerId', 'assetType', 'liabilityType', 'institutionId']).filter(attr => visibleMultipicklistAttrNames.includes(attr));

  return (
    <>
      <FDStack direction="row" spacing={1}>
        {visibleMultipicklistAttrNames.length > 0 && (getFiltersButton())}
        {isFiltering && !isMobileOrMC ? (
          <React.Fragment>
            <MetaResourceRenderer
              resource={resource}
              layout={HouseholdBalanceSheetFilterLozengesLayout}
              metaOverrideSet={lozengesMetaOverrideSet}
            />
            <FDTextButton
              label="Clear Filters"
              onClick={onClickClearFilters}
              size="sm"
              icon={'close'}
            />
          </React.Fragment>
        ) : null}
      </FDStack>
      <FDDialog
        open={open}
        onClose={() => setOpen(false)}
        closeOnBackdropClick
        fullScreen={isMobile}
        dialogWidth={bottomRow.length > 2 ? 'md' : 'sm'}
        color="subtle"
      >
        <FDDialogTitle value="Filters" />
        <FDDialogContent>
          <FDStack direction="column" spacing={3}>
            {hidePrompt ? null : <FDBodyText value={prompt} />}
            {additionalFields.map(field => (
              <MetaControlField key={field.name} attr={field.name} direction="column" />
            ))}
            {topRow.length > 0 ? topRow.map(attr => (
              <MetaControlField key={attr} attr={attr} direction="row" />
            )) : null}
            {bottomRow.length > 0 ? (
              <FDStack direction="row" spacing={3}>
                {bottomRow.map((attr, index) => (
                  <Fragment key={attr}>
                    <MetaControlField attr={attr} direction="column" />
                    {index + 1 === bottomRow.length ? null : <div className={classes.verticalDivider} />}
                  </Fragment>
                ))}
              </FDStack>
            ) : null}
            <MetaLayoutRemainingFields noAccordion />
          </FDStack>
        </FDDialogContent>
        <FDDialogActions>
          <FDPrimaryActionButton label="Apply" onClick={onSubmit} loading={loading} />
          <FDSecondaryActionButton
            label="Reset"
            icon="reset"
            iconPosition="end"
            onClick={onClickResetFilters}
          />
        </FDDialogActions>
      </FDDialog>
    </>
  );
};

HouseholdBalanceSheetFilterLayout.defaultProps = {
  open: false,
};

HouseholdBalanceSheetFilterLayout.propTypes = {
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  prompt: PropTypes.string.isRequired,
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool,
};
